import { useMutation } from '@apollo/client';
import React from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from '@eluve/components';

import { deleteTemplateBundleDefaultMutation } from './bundle-defaults.operations';

export interface BundleDefaultDisplayRow {
  id: string;
  templateBundleId: string;
  bundleDisplayName: string;
  specialty: string | null;
  language: string | null;
  updatedAt: string;
}

interface DeleteBundleDefaultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: BundleDefaultDisplayRow & { scopeLabel: string };
  onDeleted: () => void;
}

export const DeleteBundleDefaultDialog: React.FC<
  DeleteBundleDefaultDialogProps
> = ({ open, onOpenChange, row, onDeleted }) => {
  const toast = useToast();

  const [deleteDefault, { loading }] = useMutation(
    deleteTemplateBundleDefaultMutation,
    {
      context: eluveAdminHasuraContext,
      variables: { id: row.id },
    },
  );

  const handleDelete = async () => {
    try {
      const result = await deleteDefault();
      if (result.errors?.length) {
        toast.error(
          `Failed to delete default: ${result.errors.map((e) => e.message).join('; ')}`,
        );
        return;
      }
      toast.success('Default deleted');
      onDeleted();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `Failed to delete default: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  };

  const specialty = row.specialty ?? 'Any specialty';
  const language = row.language ?? 'Any language';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Bundle Default</AlertDialogTitle>
          <AlertDialogDescription>
            {row.scopeLabel} default for {specialty} / {language} →{' '}
            {row.bundleDisplayName}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleDelete();
            }}
            disabled={loading}
            className="bg-bg-negative-strong-primary hover:bg-bg-negative-strong-primary/80"
          >
            {loading ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
